import { useMemo, useRef, useState, useCallback, forwardRef, useImperativeHandle, type CSSProperties } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import ImagePreview from './components/ImagePreview';
import VideoPreview from './components/VideoPreview';
import AudioPreview from './components/AudioPreview';
import PDFPreview from './components/PDFPreview';
import YouTubePreview from './components/YouTubePreview';
import MediaPreviewModal from './components/MediaPreviewModal';
import CommunicationAudioPlayer from '../audio-preview/CommunicationAudioPlayer';
import PrecisionAudioPlayer from '../audio-preview/PrecisionAudioPlayer';
import { MediaPreviewProps, MediaPreviewHandle, MediaType, DeviceState } from '../../types/media-preview';
import { getFileIconFromExtension, getFileIconFromMediaType } from '../file-icon/fileIcon';

// メディアタイプの判定用マップ
const MEDIA_TYPES: Record<string, MediaType> = {
  // 画像
  jpg: 'image',
  jpeg: 'image',
  png: 'image',
  gif: 'image',
  webp: 'image',
  // 動画
  mp4: 'video',
  webm: 'video',
  mov: 'video',
  m3u8: 'video',
  // オーディオ
  mp3: 'audio',
  wav: 'audio',
  aac: 'audio',
  m4a: 'audio',
  ogg: 'audio',
  flac: 'audio',
  // PDF
  pdf: 'pdf',
};

const PreviewWrapper = styled.div<{
  $isMobile?: boolean;
  $disableHoverEffect?: boolean;
  $fillMode?: boolean;
  $noRadius?: boolean;
}>`
  position: relative;
  border-radius: ${props => props.$noRadius ? '0' : '6px'};
  overflow: hidden;
  background: transparent;
  transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  
  /* sizeModeに応じて子コンポーネントがサイズを制御するため、コンテナは柔軟に対応 */
  width: 100%;
  max-width: 100%;
  /* fill モードは親の高さいっぱいに伸ばす（親が definite height を持つ前提）。
     子（Video/Image コンテナ）も fill 時に height:100% を引き継ぐ。 */
  height: ${props => props.$fillMode ? '100%' : 'fit-content'};
  max-height: ${props => props.$fillMode ? '100%' : '100dvh'};

  /* デスクトップでのみホバーエフェクトを適用（disableHoverEffectで無効化可能） */
  ${props => !props.$isMobile && !props.$disableHoverEffect && `
    @media (min-width: 769px) {
      &::before {
        content: '';
        position: absolute;
        top: -100%;
        left: -100%;
        right: -100%;
        bottom: -100%;
        background: radial-gradient(
          circle at var(--mouse-x, 50%) var(--mouse-y, 50%),
          rgba(255, 255, 255, 0.4) 0%,
          rgba(255, 255, 255, 0.2) 25%,
          rgba(255, 255, 255, 0.1) 50%,
          transparent 70%
        );
        opacity: 0;
        transition: opacity 0.3s ease;
        transform: translate(0, 0);
        pointer-events: none;
        z-index: 2;
      }

      &:hover::before {
        opacity: 1.0;
      }
    }
  `}
`;

const CompactFileDisplay = styled.div`
  padding: 4px;
  background-color: var(--soremo-background);
  border: 1px solid var(--soremo-border);
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 4px;
  width: 100%;

  .compact-icon {
    font-size: 20px;
    color: var(--soremo-light-gray);
    flex-shrink: 0;
  }

  .compact-filename {
    flex: 1;
    word-break: break-all;
  }
`;

const MediaPreview = forwardRef<MediaPreviewHandle, MediaPreviewProps>(({
  src,
  thumbnailSrc = null,
  mediaType: providedMediaType = null,
  sizeMode = { mode: 'auto', objectFit: 'cover' },
  showMenu = false,
  showDownloadIcon = false,
  showStatusBar = false,
  showWaveform = false,
  waveformUrl,
  pauseWhenOffscreen = true,
  optionLabel,
  optionSelected,
  onOptionSelect,
  autoPlay = false,
  hoverPlay = false,
  onLoaded = null,
  controls = false,
  loop = true,
  className = '',
  style = {},
  onError,
  onPlayStateChange,
  fileName,
  fileSize,
  createdAt,
  disableHoverEffect = false,
  enableClickPreview = false,
  menuOutside = false,
  onDownload,
  downloadUrl,
  variant = 'default',
  fileId,
  acrScanStatus,
  acrScanResult,
  acrScannedAt,
  showProgressBar,
  showRemainingTimeOnPlay,
  showRestartOnPause,
  extraMenuItems,
  overrideDownload,
  downloadMenuLabel,
  outsideLeftContent,
  compactAudio = false,
  pdfInitialPage,
  glowTrigger,
}, ref) => {
  const [showPreviewModal, setShowPreviewModal] = useState(false);

  // 子メディアコンポーネントへのref（再生制御用）
  const childMediaRef = useRef<MediaPreviewHandle>(null);

  // 外部へのAPI公開（子コンポーネントのrefをパススルー）
  useImperativeHandle(ref, () => ({
    play: async () => {
      await childMediaRef.current?.play();
    },
    pause: () => {
      childMediaRef.current?.pause();
    },
    seek: (seconds: number) => {
      childMediaRef.current?.seek(seconds);
    },
    seekTo: (seconds: number) => {
      childMediaRef.current?.seekTo(seconds);
    },
    getCurrentTime: () => {
      return childMediaRef.current?.getCurrentTime() ?? 0;
    },
    getDuration: () => {
      return childMediaRef.current?.getDuration() ?? 0;
    },
    clickToggle: () => {
      childMediaRef.current?.clickToggle();
    },
    getMediaElement: () => {
      return childMediaRef.current?.getMediaElement?.() ?? null;
    },
    getFps: () => {
      return childMediaRef.current?.getFps?.() ?? null;
    },
  }));

  // Reduxからデバイス状態を取得
  const deviceState = useSelector((state: { device: DeviceState }) => state.device);
  const { isMobile } = deviceState;

  // メディアタイプの判定
  const mediaType = useMemo((): MediaType => {
    if (providedMediaType) return providedMediaType;
    
    // srcがnullまたは空の場合は早期リターン
    if (!src) {
      return 'image'; // デフォルト値を返す
    }
    
    // URLからクエリパラメータを除去して拡張子を取得
    const urlWithoutQuery = src.split('?')[0];
    const extension = urlWithoutQuery.split('.').pop()?.toLowerCase();
    
    return extension ? (MEDIA_TYPES[extension] || 'image') : 'image';
  }, [providedMediaType, src]);

  // サイズモードを子コンポーネント用の形式に変換
  const computedSizeProps = useMemo(() => {
    const mode = sizeMode.mode;
    
    switch (mode) {
      case 'fixed-width':
        return {
          mode,
          width: sizeMode.width,
          maxHeight: sizeMode.maxHeight,
          objectFit: 'contain' as const,
        };
      
      case 'fixed-height':
        return {
          mode,
          height: sizeMode.height,
          maxWidth: sizeMode.maxWidth,
          objectFit: 'cover' as const,
        };
      
      case 'fill':
        return {
          mode: 'fill' as const,
          objectFit: sizeMode.objectFit || 'contain' as const,
        };
      
      case 'auto':
      default:
        return {
          mode: 'auto' as const,
          maxWidth: sizeMode.maxWidth,
          maxHeight: sizeMode.maxHeight,
          objectFit: sizeMode.objectFit || 'cover' as const,
        };
    }
  }, [sizeMode]);

  // 共通propsをメモ化
  const commonProps = useMemo(() => {
    if (!src) return null;
    return {
      src,
      thumbnailSrc,
      showMenu,
      showDownloadIcon,
      showStatusBar,
      className,
      style,
      sizeMode: computedSizeProps,
      fileName,
      fileSize,
      createdAt,
      onDownload,
      downloadUrl,
      fileId,
      acrScanStatus,
      acrScanResult,
      acrScannedAt,
    };
  }, [src, thumbnailSrc, showMenu, showDownloadIcon, showStatusBar, className, style, computedSizeProps, fileName, fileSize, createdAt, onDownload, downloadUrl, fileId, acrScanStatus, acrScanResult, acrScannedAt]);

  // メディアコンポーネントの選択とレンダリング（refをパススルーするためuseMemoの外で描画）
  const renderMediaComponent = () => {
    if (!commonProps) return null;

    switch (mediaType) {
      case 'image':
        return <ImagePreview {...commonProps} onLoaded={onLoaded || undefined} menuOutside={menuOutside} />;
      case 'video':
        return (
          <VideoPreview
            ref={childMediaRef}
            {...commonProps}
            autoPlay={autoPlay}
            hoverPlay={hoverPlay}
            controls={controls}
            loop={loop}
            onLoaded={onLoaded || undefined}
            onPlayStateChange={onPlayStateChange}
            disableClickToPlay={enableClickPreview}
            menuOutside={menuOutside}
            showProgressBar={showProgressBar}
            showRemainingTimeOnPlay={showRemainingTimeOnPlay}
            showRestartOnPause={showRestartOnPause}
            extraMenuItems={extraMenuItems}
            overrideDownload={overrideDownload}
            downloadMenuLabel={downloadMenuLabel}
            outsideLeftContent={outsideLeftContent}
          />
        );
      case 'audio':
        // コンパクト表示（Messenger 等）はコミュニケーションプレビューへ
        if (compactAudio) {
          return (
            <CommunicationAudioPlayer
              ref={childMediaRef}
              src={commonProps.src}
              waveformUrl={waveformUrl}
              fileName={fileName}
              downloadUrl={downloadUrl}
              onDownload={onDownload}
              onOpenPreview={enableClickPreview ? handleOpenAudioPreview : undefined}
              showMenu={showMenu}
              onPlayStateChange={onPlayStateChange}
              fileId={fileId}
              acrScanStatus={acrScanStatus}
              acrScanResult={acrScanResult}
              acrScannedAt={acrScannedAt}
              className={className}
              style={style as CSSProperties}
            />
          );
        }
        // 波形表示（Preview 画面・モーダル等）はプレシジョンプレビューへ
        if (showWaveform) {
          return (
            <PrecisionAudioPlayer
              ref={childMediaRef}
              src={commonProps.src}
              waveformUrl={waveformUrl}
              fileName={fileName}
              downloadUrl={downloadUrl}
              onDownload={onDownload}
              showMenu={showMenu}
              pauseWhenOffscreen={pauseWhenOffscreen}
              optionLabel={optionLabel}
              optionSelected={optionSelected}
              onOptionSelect={onOptionSelect}
              onLoaded={onLoaded || undefined}
              onPlayStateChange={onPlayStateChange}
              fileId={fileId}
              acrScanStatus={acrScanStatus}
              acrScanResult={acrScanResult}
              acrScannedAt={acrScannedAt}
              className={className}
              style={style as CSSProperties}
            />
          );
        }
        // 波形なし: サムネイル表示（ブラウジング / カード向け）
        // hoverPlay は渡さない。音源はホバーで鳴らさず、クリックで開く
        // フローティングプレイヤーバーに再生を委ねる（動画のホバー再生は維持）。
        return (
          <AudioPreview
            ref={childMediaRef}
            {...commonProps}
            onError={onError}
            onLoaded={onLoaded || undefined}
            onPlayStateChange={onPlayStateChange}
            waveformUrl={waveformUrl}
            glowTrigger={glowTrigger}
          />
        );
      case 'pdf':
        return (
          <PDFPreview
            {...commonProps}
            showControls={controls}
            onLoaded={onLoaded || undefined}
            menuOutside={menuOutside}
            disableInteraction={enableClickPreview}
            initialPage={pdfInitialPage}
          />
        );
      case 'youtube':
        return (
          <YouTubePreview
            {...commonProps}
            hoverPlay={hoverPlay}
            autoPlay={autoPlay}
            showControls={controls}
          />
        );
      default:
        return null;
    }
  };

  // クリックプレビュー対象かどうか（画像・動画・PDF・YouTube）
  const isClickPreviewable = enableClickPreview && (mediaType === 'image' || mediaType === 'video' || mediaType === 'pdf' || mediaType === 'youtube');

  const handlePreviewClick = useCallback(() => {
    if (isClickPreviewable) {
      if (mediaType === 'video') {
        onPlayStateChange?.(true);
      }
      setShowPreviewModal(true);
    }
  }, [isClickPreviewable, mediaType, onPlayStateChange]);

  const handleOpenAudioPreview = useCallback(() => {
    setShowPreviewModal(true);
  }, []);

  const handleClosePreview = useCallback(() => {
    setShowPreviewModal(false);
  }, []);

  // compact バリアント: アイコン + ファイル名のシンプル表示
  // （フック呼び出し順を保つため、分岐はすべてのフック定義の後に置く）
  if (variant === 'compact') {
    const displayName = fileName || (src ? src.split('/').pop()?.split('?')[0] : '') || '';
    // compact 表示のタイプアイコン:
    //  - providedMediaType が明示されていればそれを優先（image/video/audio/pdf）
    //  - それ以外は拡張子で判定する。
    //    mediaType は未知拡張子を 'image' にフォールバックしてしまうため、
    //    ここでは直接 fileName / src の拡張子から判定し、
    //    doc / xlsx / zip 等も専用アイコン（description / table_chart / folder_zip）に解決する。
    const icon = providedMediaType
      ? getFileIconFromMediaType(providedMediaType)
      : getFileIconFromExtension(fileName ?? src);
    return (
      <CompactFileDisplay className={className}>
        <span className="material-symbols-rounded compact-icon">{icon}</span>
        <span className="bodytext-11 u-line-height-150 compact-filename">{displayName}</span>
      </CompactFileDisplay>
    );
  }

  return (
    <>
      <PreviewWrapper 
        className={className} 
        style={{
          ...style,
          ...(isClickPreviewable ? { cursor: 'pointer' } : {}),
        }}
        $isMobile={isMobile}
        $disableHoverEffect={disableHoverEffect}
        $fillMode={computedSizeProps.mode === 'fill'}
        $noRadius={isMobile && !!showProgressBar}
        onClick={isClickPreviewable ? handlePreviewClick : undefined}
      >
        {renderMediaComponent()}
      </PreviewWrapper>

      {showPreviewModal && src && (
        <MediaPreviewModal
          src={src}
          mediaType={mediaType}
          onClose={handleClosePreview}
          fileName={fileName}
          waveformUrl={waveformUrl}
          fileId={fileId}
          acrScanStatus={acrScanStatus}
          acrScanResult={acrScanResult}
          acrScannedAt={acrScannedAt}
          fileSize={fileSize}
          createdAt={createdAt}
          onDownload={onDownload}
          downloadUrl={downloadUrl}
        />
      )}
    </>
  );
});

MediaPreview.displayName = 'MediaPreview';

export default MediaPreview;
